import React, { useCallback, useEffect, useState } from 'react';
import {
  Table, Tag, Button, Space, Input, Select, Row, Col, Tooltip, Popconfirm,
} from 'antd';
import {
  SearchOutlined, ReloadOutlined, PlusOutlined, EyeOutlined,
  FilePdfOutlined, SendOutlined, StopOutlined,
  SafetyCertificateOutlined, CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined,
} from '@ant-design/icons';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import KpiCards from '../../components/KpiCards';
import PageHeader from '../../components/PageHeader';
import {
  listerAttestations, revoquer, renvoyer, ouvrirPdf,
  STATUTS_ATTESTATION_DIGITALE,
} from '../../services/attestationDigitaleService';
import EmettreAttestationModal from './EmettreAttestationModal';
import RenvoyerAttestationModal from './RenvoyerAttestationModal';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';

const { Option } = Select;

export default function ListAttestationsDigitales() {
  const navigate = useNavigate();
  const { t } = useTranslation('attestationsDigitales');

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });

  const [search, setSearch] = useState('');
  const [filtreStatut, setFiltreStatut] = useState(null);

  const [modalEmettre, setModalEmettre] = useState(false);
  const [renvoyerItem, setRenvoyerItem] = useState(null);

  const [stats, setStats] = useState({ total: 0, actives: 0, revoquees: 0, expirees: 0 });

  const load = useCallback(async (page = 0, size = 20) => {
    setLoading(true);
    try {
      const params = {};
      if (filtreStatut) params.statut = filtreStatut;
      if (search) params.search = search;
      const result = await listerAttestations(page, size, params);
      setData(result.items);
      setPagination((p) => ({ ...p, total: result.totalElements, current: page + 1 }));
    } catch {
      toast.error(i18n.t('attestationsDigitales:liste.erreurChargement'));
    } finally {
      setLoading(false);
    }
  }, [filtreStatut, search]);

  const loadStats = useCallback(async () => {
    try {
      const result = await listerAttestations(0, 500);
      const all = result.items;
      setStats({
        total: result.totalElements,
        actives: all.filter((a) => a.statut === 'ACTIVE').length,
        revoquees: all.filter((a) => a.statut === 'ANNULEE').length,
        expirees: all.filter((a) => a.statut === 'EXPIREE').length,
      });
    } catch { /* silencieux */ }
  }, []);

  useEffect(() => { load(0); loadStats(); }, [load, loadStats]);

  const handleTableChange = (p) => load(p.current - 1, p.pageSize);

  const handleRevoquer = async (record) => {
    try {
      await revoquer(record.trackingId, { motif: 'Révoqué par BNCB' });
      toast.success(t('messages.revoquee'));
      load(pagination.current - 1);
      loadStats();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('messages.erreurRevocation'));
    }
  };

  const handleOuvrirPdf = async (record) => {
    try {
      await ouvrirPdf(record.digitale?.tokenTelechargement);
    } catch {
      toast.error(t('messages.erreurPdf'));
    }
  };

  const columns = [
    {
      title: t('liste.colonnes.numero'),
      dataIndex: 'numeroAttestation',
      key: 'numero',
      render: (val, rec) => (
        <Button type="link" style={{ padding: 0, fontWeight: 600 }}
          onClick={() => navigate(`/attestations-digitales/${rec.trackingId}`)}>
          {val}
        </Button>
      ),
    },
    {
      title: t('liste.colonnes.souscripteur'),
      key: 'souscripteur',
      render: (_, rec) => `${rec.souscripteurNom || ''} ${rec.souscripteurPrenom || ''}`.trim(),
    },
    {
      title: t('liste.colonnes.immatriculation'),
      dataIndex: 'vehiculeImmatriculation',
      key: 'immat',
    },
    {
      title: t('liste.colonnes.compagnie'),
      key: 'compagnie',
      render: (_, rec) => rec.compagnieRaisonSociale || '-',
    },
    {
      title: t('liste.colonnes.effetEcheance'),
      key: 'dates',
      render: (_, rec) => (
        <span style={{ fontSize: 12 }}>
          {rec.dateEffet} → {rec.dateEcheance}
        </span>
      ),
    },
    {
      title: t('liste.colonnes.statut'),
      dataIndex: 'statut',
      key: 'statut',
      render: (val) => {
        const s = STATUTS_ATTESTATION_DIGITALE[val] || { label: val, color: 'default' };
        return <Tag color={s.color}>{s.label}</Tag>;
      },
    },
    {
      title: t('liste.colonnes.actions'),
      key: 'actions',
      width: 160,
      render: (_, rec) => (
        <Space size={4}>
          <Tooltip title={t('liste.detail')}>
            <Button size="small" icon={<EyeOutlined />}
              onClick={() => navigate(`/attestations-digitales/${rec.trackingId}`)} />
          </Tooltip>
          {rec.digitale?.tokenTelechargement && (
            <Tooltip title="PDF">
              <Button size="small" icon={<FilePdfOutlined />}
                onClick={() => handleOuvrirPdf(rec)} />
            </Tooltip>
          )}
          {rec.statut === 'ACTIVE' && (
            <>
              <Tooltip title={t('actions.renvoyer')}>
                <Button size="small" icon={<SendOutlined />}
                  onClick={() => setRenvoyerItem(rec)} />
              </Tooltip>
              <Popconfirm
                title={t('liste.revoquerTitre')}
                onConfirm={() => handleRevoquer(rec)}
                okText={t('actions.revoquer')} cancelText={t('actions.annuler')} okButtonProps={{ danger: true }}>
                <Tooltip title={t('actions.revoquer')}>
                  <Button size="small" danger icon={<StopOutlined />} />
                </Tooltip>
              </Popconfirm>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Layout title={t('liste.titreLayout')}>
      {/* La liste est paginee et filtree cote serveur : le compte doit venir du
          total renvoye par l'API, pas de la page courante. */}
      <PageHeader
        titre={t('liste.titre')}
        icone={<SafetyCertificateOutlined />}
        compte={pagination.total}
        unite={t('liste.unite')}
        unitePluriel={t('liste.unitePluriel')}
        actions={(
          <Button
            type="primary" icon={<PlusOutlined />}
            style={{ background: '#1a6b3c', borderColor: '#1a6b3c' }}
            onClick={() => setModalEmettre(true)}>
            {t('liste.emettre')}
          </Button>
        )}
      />

      <KpiCards
        items={[
          { label: t('liste.kpi.totalEmises'), value: stats.total, icon: <SafetyCertificateOutlined />, color: '#1a6b3c', bg: '#e8f5ed' },
          { label: t('liste.kpi.actives'), value: stats.actives, icon: <CheckCircleOutlined />, color: '#166534', bg: '#dcfce7' },
          { label: t('liste.kpi.revoquees'), value: stats.revoquees, icon: <CloseCircleOutlined />, color: '#991b1b', bg: '#fee2e2' },
          { label: t('liste.kpi.expirees'), value: stats.expirees, icon: <ClockCircleOutlined />, color: '#6b7280', bg: '#f3f4f6' },
        ]}
      />

      <div className="cb-card" style={{ padding: 16 }}>
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={24} md={10}>
            <Input
              placeholder={t('liste.recherche')}
              prefix={<SearchOutlined />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onPressEnter={() => load(0)}
              allowClear
            />
          </Col>
          <Col xs={12} md={6}>
            <Select
              placeholder={t('liste.statut')} allowClear style={{ width: '100%' }}
              value={filtreStatut}
              onChange={(v) => setFiltreStatut(v)}>
              {Object.entries(STATUTS_ATTESTATION_DIGITALE).map(([k, v]) => (
                <Option key={k} value={k}>{v.label}</Option>
              ))}
            </Select>
          </Col>
          <Col xs={12} md={4}>
            <Button icon={<ReloadOutlined />} onClick={() => load(0)} block>{t('actions.actualiser')}</Button>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={data}
          rowKey="trackingId"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => t('liste.total', { n: total }),
          }}
          onChange={handleTableChange}
          size="small"
          scroll={{ x: 900 }}
        />
      </div>

      <EmettreAttestationModal
        open={modalEmettre}
        onClose={() => setModalEmettre(false)}
        onEmis={() => { setModalEmettre(false); load(0); loadStats(); }}
      />

      <RenvoyerAttestationModal
        open={!!renvoyerItem}
        attestation={renvoyerItem}
        onClose={() => setRenvoyerItem(null)}
        onEnvoye={() => { setRenvoyerItem(null); load(pagination.current - 1); }}
      />
    </Layout>
  );
}
